export const APP_CONFIG = {
  name: 'Ocean Insight Pro',
  version: '1.0.0',
  supportEmail: 'support@oceaninsightpro.com',
  privacyUrl: 'https://oceaninsightpro.com/privacy',
  termsUrl: 'https://oceaninsightpro.com/terms',
} as const;

export const API_CONFIG = {
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  cacheExpiration: 6 * 60 * 60 * 1000, // 6 hours
} as const;

export const MAP_CONFIG = {
  defaultRegion: {
    latitude: 25.0,  // Center of Gulf of Mexico
    longitude: -90.0, // Center of Gulf of Mexico
    latitudeDelta: 12.0, // Shows from South Texas to Florida
    longitudeDelta: 15.0, // Shows full width of Gulf
  },
  globalBounds: {
    minLat: -90.0,
    maxLat: 90.0,
    minLon: -180.0,
    maxLon: 180.0,
  },
  debounceDelay: 1000,
  minZoomForData: 5.0, // Adjusted for larger area
  maxMarkersToRender: 100,
  maxCirclesToRender: 50,
  clusteringEnabled: true,
  animationDuration: 1000,
} as const;

export const DATA_TYPE_CONFIG = {
  sst: {
    key: 'sst',
    label: 'Sea Surface Temperature',
    unit: '°C',
    icon: 'device-thermostat',
    color: '#FF6B6B',
    range: { min: 0, max: 35 },
    gradient: {
      colors: ['#0000FF', '#00FFFF', '#00FF00', '#FFFF00', '#FF0000'],
      positions: [0.0, 0.25, 0.5, 0.75, 1.0],
    },
  },
  chlorophyll: {
    key: 'chlorophyll',
    label: 'Chlorophyll-a',
    unit: 'mg/m³',
    icon: 'eco',
    color: '#4ECDC4',
    range: { min: 0.01, max: 10 },
    gradient: {
      colors: ['#000080', '#0000FF', '#00FF00', '#FFFF00', '#FF0000'],
      positions: [0.0, 0.2, 0.4, 0.7, 1.0],
    },
  },
  ssh: {
    key: 'ssh',
    label: 'Sea Surface Height',
    unit: 'm',
    icon: 'waves',
    color: '#45B7D1',
    range: { min: -0.5, max: 0.5 },
    gradient: {
      colors: ['#00008B', '#4169E1', '#FFFFFF', '#FF6347', '#8B0000'],
      positions: [0.0, 0.25, 0.5, 0.75, 1.0],
    },
  },
  currents: {
    key: 'currents',
    label: 'Ocean Currents',
    unit: 'm/s',
    icon: 'water',
    color: '#96CEB4',
    range: { min: 0, max: 2 },
    gradient: {
      colors: ['#FFFFFF', '#87CEEB', '#4682B4', '#000080', '#000033'],
      positions: [0.0, 0.25, 0.5, 0.75, 1.0],
    },
  },
  mld: {
    key: 'mld',
    label: 'Mixed Layer Depth',
    unit: 'm',
    icon: 'layers',
    color: '#DDA0DD',
    range: { min: 0, max: 200 },
    gradient: {
      colors: ['#FFE4E1', '#DDA0DD', '#9370DB', '#4B0082', '#000080'],
      positions: [0.0, 0.25, 0.5, 0.75, 1.0],
    },
  },
} as const;

export const CACHE_KEYS = {
  userData: '@user_data',
  authToken: '@auth_token',
  mapRegion: '@map_region',
  favorites: '@favorites',
  settings: '@settings',
} as const;

export const ERROR_MESSAGES = {
  network: 'Network error. Please check your connection.',
  auth: {
    invalidCredentials: 'Invalid email or password',
    emailInUse: 'Email already in use',
    weakPassword: 'Password should be at least 6 characters',
    userNotFound: 'User not found',
    tooManyRequests: 'Too many attempts. Please try again later.',
    networkError: 'Network error during authentication',
  },
  data: {
    fetchFailed: 'Failed to fetch data',
    saveFailed: 'Failed to save data',
    deleteFailed: 'Failed to delete data',
    invalidData: 'Invalid data format',
  },
  validation: {
    required: 'This field is required',
    email: 'Please enter a valid email',
    minLength: 'Minimum length is {min} characters',
    maxLength: 'Maximum length is {max} characters',
    numeric: 'Please enter a valid number',
  },
} as const;

export const ASYNC_STORAGE_KEYS = {
  auth: '@auth',
  user: '@user',
  settings: '@settings',
  cache: '@cache',
} as const;

export const SCREEN_NAMES = {
  // Auth Screens
  LOGIN: 'Login',
  SIGNUP: 'SignUp',
  FORGOT_PASSWORD: 'ForgotPassword',
  
  // Main Screens
  DASHBOARD: 'Dashboard',
  MAP: 'Map',
  TRIPS: 'Trips',
  PROFILE: 'Profile',
  SETTINGS: 'Settings',
  
  // Detail Screens
  TRIP_DETAILS: 'TripDetails',
  FISHING_SPOT: 'FishingSpot',
  CATCH_REPORT: 'CatchReport',
  WEATHER_DETAILS: 'WeatherDetails',
} as const;

export const REGEX_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s-()]+$/,
  coordinates: {
    latitude: /^-?([0-8]?[0-9]|90)(\.[0-9]+)?$/,
    longitude: /^-?([0-9]{1,2}|1[0-7][0-9]|180)(\.[0-9]+)?$/,
  },
} as const;

export const ANIMATION_CONFIG = {
  duration: {
    fast: 200,
    normal: 300,
    slow: 500,
  },
  easing: {
    standard: 'ease-in-out',
    accelerate: 'ease-in',
    decelerate: 'ease-out',
  },
} as const;

export const PERMISSIONS = {
  location: 'location',
  camera: 'camera',
  notifications: 'notifications',
} as const;